/**
 * NOVA-023 — BreakGlassReviewScreen. The manager/medical-director queue of pending duty-to-warn
 * overrides awaiting their mandatory post-hoc review (VAL-3). Each break-glass invoked at the bench
 * lands `reviewed=false`; this screen lists them (oldest first, FIFO) and opens a review modal that
 * captures the recorded verdict + notes — sealed server-side in the tamper-evident chain. The row drops
 * out of the queue once reviewed (the GET re-derives reviewed=false).
 *
 * Reaching the screen + every review action requires `validation.valeurcritique.review` (module-level +
 * a local ForbiddenState guard; the server re-checks). The 5 states: loading/error/empty + offline + forbidden.
 */
import { useState } from "react";
import { Badge, Button, Card, EmptyState, ErrorState, ForbiddenState, LoadingState, OfflineState, Table, useToast, type Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import type { ApiError } from "@/api";
import { token } from "@/design/theme";
import { CAP, type Id, type ValidationBreakGlass } from "../types";
import { useBreakGlassReviewQueue, useReviewBreakGlass } from "../hooks/useValidation";
import { BreakGlassReviewModal } from "../components/BreakGlassReviewModal";

export function BreakGlassReviewScreen() {
  const { t } = useT();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canReview = useCan(CAP.review);

  const { data, isLoading, isError, error, refetch } = useBreakGlassReviewQueue();
  const review = useReviewBreakGlass();
  const [active, setActive] = useState<ValidationBreakGlass | null>(null);

  if (!online) return <OfflineState cached={false} />;
  if (!canReview) return <ForbiddenState title={t("validation.bgreview.title")} requiredCapabilities={[CAP.review]} />;
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => void refetch()} />;

  const rows = data ?? [];

  const submitReview = async (input: { outcome: string; notes?: string }) => {
    if (!active) return;
    try {
      await review.mutateAsync({ id: active.id as Id, ...input });
      toast.success(t("validation.bgreview.done"));
      setActive(null);
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("validation.bgreview.error"));
    }
  };

  const columns: Column<ValidationBreakGlass>[] = [
    { key: "id", header: t("validation.bgreview.col.id"), mono: true, cell: (b) => <span className="nv-mono nv-ltr-island">#{String(b.id)}</span> },
    { key: "escalade", header: t("validation.bgreview.col.escalade"), mono: true, cell: (b) => <span className="nv-mono nv-ltr-island">#{String(b.escalade_critique_id)}</span> },
    { key: "justification", header: t("validation.bgreview.col.justification"), cell: (b) => <span style={{ display: "block", maxInlineSize: "36ch" }}>{b.justification}</span> },
    { key: "status", header: t("validation.bgreview.col.status"), cell: () => <Badge tone="warn">{t("validation.bgreview.pending")}</Badge> },
    {
      key: "action",
      header: "",
      align: "end",
      cell: (b) => (
        <Button size="sm" onClick={() => setActive(b)}>
          {t("validation.bgreview.review")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <header style={{ display: "flex", flexDirection: "column", gap: token.s1 }}>
        <h1 style={{ fontSize: token.fsH1, margin: 0 }}>{t("validation.bgreview.title")}</h1>
        <span style={{ color: token.slate, fontSize: token.fsLabel }}>{t("validation.bgreview.subtitle")}</span>
      </header>

      <Card title={t("validation.bgreview.queue")} padded={rows.length === 0}>
        {rows.length === 0 ? (
          <EmptyState glyph="✓" title={t("validation.bgreview.empty.title")} description={t("validation.bgreview.empty.desc")} />
        ) : (
          <Table<ValidationBreakGlass> columns={columns} rows={rows} rowKey={(b) => String(b.id)} />
        )}
      </Card>

      <BreakGlassReviewModal
        open={active !== null}
        breakGlass={active}
        onClose={() => setActive(null)}
        onConfirm={submitReview}
        submitting={review.isPending}
      />
    </div>
  );
}
